import { round2 } from '../utils';
import { SaleProduct, VariantOption } from './types';

/**
 * The sale prices a term one of two ways, and which one it used is a property of the
 * data rather than a setting.
 *
 * `term_prices` is an absolute price per term id and wins when it has an entry. When it
 * does not, the term carries a `percent` and the price is the base price adjusted by it
 * — the same arithmetic the legacy component did, kept because the numbers on the tile
 * have to keep matching what `cart.php` charges.
 */
export function termPercentage(term: VariantOption | null | undefined): number {
  if (!term || term.percent === undefined || term.percent === null) {
    return 0;
  }

  const percent = parseFloat(String(term.percent));

  return Number.isFinite(percent) ? percent : 0;
}

export function applyTermPercentage(value: number, term: VariantOption | null | undefined): number {
  return round2(value * (1 + termPercentage(term) / 100));
}

export function calculatePrice(term: VariantOption | null | undefined, product: SaleProduct): number {
  if (term && product.term_prices) {
    const price = product.term_prices[String(term.id)];

    if (price !== undefined && price !== null) {
      return round2(Number(price));
    }
  }

  return applyTermPercentage(product.price, term);
}

/**
 * The term a tile opens on.
 *
 * `highestTerm` is the sale-wide `ht` flag, which asks for the longest term rather than
 * the default one. Otherwise the catalogue's `default === 'Y'` wins, and the first term
 * is the fallback.
 *
 * The legacy version's default-term search was a `forEach` with a `return` inside it,
 * which returns from the callback and not from the function — so it never found
 * anything and every product opened on its first term regardless of the flag. Fixed
 * here with a `find`; the visible effect is that a catalogue default is now honoured.
 */
export function findDefaultTerm(product: SaleProduct, highestTerm = false): VariantOption | null {
  const terms = product.restricted_terms || [];

  if (terms.length === 0) {
    return null;
  }

  if (highestTerm) {
    return terms[terms.length - 1];
  }

  return terms.find((term) => term.default === 'Y') ?? terms[0];
}

/**
 * Issues delivered for the chosen term, and the price per issue.
 *
 * `issues` on the product is the base term's count and scales with the same percentage
 * the price does, so a 2-year term at +100% is twice the issues as well as twice the
 * price and the per-issue figure stays honest.
 */
export function issuesForTerm(product: SaleProduct, term: VariantOption | null): number | null {
  if (!product.attributes.issues) {
    return null;
  }

  return round2(product.attributes.issues * (1 + termPercentage(term) / 100));
}

export function pricePerIssue(price: number, issues: number | null): number | null {
  if (!issues) {
    return null;
  }

  return round2(price / issues);
}
